import { useState, useCallback, ReactNode, useMemo } from 'react';
import { AnalyticsCard } from './AnalyticsCard';
import { CompactControls } from './CompactControls';
import { AnalyticsErrorBoundary } from './AnalyticsErrorBoundary';
import { ControlOption } from './types';

type WidgetView = 'chart' | 'table' | 'log';

interface DashboardWidgetProps<T> {
  title: string | ((metric: 'sessions' | 'minutes') => string);
  subtitle?: string;
  data: T[] | undefined;
  isLoading: boolean;
  error: Error | null;
  emptyMessage?: string;
  defaultMetric?: 'sessions' | 'minutes';
  defaultView?: WidgetView;
  metricOptions: ControlOption<'sessions' | 'minutes'>[];
  viewOptions: ControlOption<WidgetView>[];
  dropdownViewOptions?: Array<'chart' | 'table'>;
  renderChart: (data: T[], metric: 'sessions' | 'minutes', onCardClick: () => void) => ReactNode;
  renderTable: (data: T[], onItemClick?: (item: T) => void) => ReactNode;
  renderLog?: () => ReactNode;
  modalComponent?: ReactNode;
  onItemClick?: (item: T) => void;
  onCardClick?: () => void;
  onResetView?: () => void;
  className?: string;
  exportData?: unknown[] | ((activeView: WidgetView) => unknown[] | undefined);
  exportFileName?: string | ((activeView: WidgetView) => string);
  accent?: string;
}

function DashboardWidgetContent<T>({
  title,
  subtitle,
  data,
  isLoading,
  error,
  emptyMessage = 'No data available',
  defaultMetric = 'sessions',
  defaultView = 'chart',
  metricOptions,
  viewOptions,
  dropdownViewOptions,
  renderChart,
  renderTable,
  renderLog,
  modalComponent,
  onItemClick,
  onCardClick,
  onResetView,
  className = '',
  exportData,
  exportFileName,
  accent,
}: DashboardWidgetProps<T>) {
  const [activeView, setActiveView] = useState<WidgetView>(defaultView);
  const [showMetric, setShowMetric] = useState<'sessions' | 'minutes'>(defaultMetric);

  const hasLogView = useMemo(
    () => viewOptions?.some(option => option.value === 'log') ?? false,
    [viewOptions],
  );

  const metricControlOptions = hasLogView && activeView === 'log' ? [] : metricOptions || [];

  const resolvedExportData = useMemo(() => {
    if (typeof exportData === 'function') {
      return exportData(activeView);
    }
    return exportData;
  }, [exportData, activeView]);

  const resolvedExportFileName = useMemo(() => {
    if (typeof exportFileName === 'function') {
      return exportFileName(activeView);
    }
    return exportFileName;
  }, [exportFileName, activeView]);

  const handleCardClick = useCallback(() => {
    onCardClick?.();
  }, [onCardClick]);

  const handleItemClick = useCallback(
    (item: T) => {
      onItemClick?.(item);
    },
    [onItemClick],
  );

  const handleResetView = useCallback(() => {
    if (onResetView) {
      onResetView();
    } else {
      const chartOption = viewOptions?.find(option => option.value === 'chart');
      if (chartOption) {
        setActiveView('chart' as WidgetView);
      }
    }
  }, [onResetView, viewOptions]);

  return (
    <>
      <AnalyticsCard
        title={typeof title === 'function' ? title(showMetric) : title}
        subtitle={subtitle}
        isLoading={isLoading}
        error={error}
        emptyMessage={emptyMessage}
        className={className}
        exportData={resolvedExportData}
        exportFileName={resolvedExportFileName}
        accent={accent}
        headerActions={
          <CompactControls
            metricOptions={metricControlOptions}
            viewOptions={viewOptions || []}
            dropdownViewOptions={dropdownViewOptions}
            selectedMetric={showMetric}
            selectedView={activeView}
            onMetricChange={setShowMetric}
            onViewChange={setActiveView}
            onResetView={handleResetView}
            showBackButton={activeView === 'log'}
          />
        }
        onBodyClick={handleCardClick}
      >
        <div>
          {activeView === 'chart'
            ? renderChart?.(data || [], showMetric, handleCardClick)
            : activeView === 'table'
              ? renderTable?.(data || [], handleItemClick)
              : (renderLog?.() ?? null)}
        </div>
      </AnalyticsCard>

      {modalComponent}
    </>
  );
}

export function DashboardWidget<T>(props: DashboardWidgetProps<T>) {
  return (
    <AnalyticsErrorBoundary>
      <DashboardWidgetContent {...props} />
    </AnalyticsErrorBoundary>
  );
}
